import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { UserCog, Eye } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { rememberImpersonation } from "@/lib/recentImpersonations";
import LiveViewModal from "@/components/dashboard/LiveViewModal";

// Impersonate any user (owner/superadmin only). After a successful call to
// /auth/impersonate/{user_id}, we refresh AuthContext so every downstream
// component re-renders with the target user's role + permissions.
const ImpersonatePanel = ({ onSwitched }) => {
  const { user, checkAuth } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switchingId, setSwitchingId] = useState(null);
  const [live, setLive] = useState(null); // { sessionId, targetName }

  const startLive = async (u) => {
    try {
      const { data } = await api.post("/liveview/start", { target_user_id: u.user_id });
      setLive({ sessionId: data.session_id, targetName: data.target_name });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start live view");
    }
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get("/users");
      setUsers(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const impersonate = async (u) => {
    setSwitchingId(u.user_id);
    try {
      await api.post(`/auth/impersonate/${u.user_id}`);
      rememberImpersonation(u);
      toast.success(`Now viewing as ${u.name || u.email}`);
      // Full reload so every mounted tab/component re-renders with the
      // target user's role and permissions — a `checkAuth()` alone leaves
      // already-mounted views on their previous state.
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impersonation failed");
      setSwitchingId(null);
    }
  };

  if (loading) return <p className="py-6 text-sm text-white/50">Loading users…</p>;

  const myId = user?.real_user?.user_id || user?.user_id;

  return (
    <div className="space-y-3" data-testid="impersonate-panel">
      <p className="text-xs text-white/40">
        Step into another user&apos;s shoes to preview exactly what they see. A yellow banner will appear
        at the top of the app; hit <span className="font-semibold text-white">Exit</span> there to return.
      </p>
      {users
        .filter((u) => u.user_id !== myId)
        .map((u) => (
          <div
            key={u.user_id}
            data-testid="impersonate-row"
            className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-3"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-white">{u.name || "(no name)"}</p>
              <p className="truncate font-mono text-[11px] text-white/40">{u.email}</p>
              <p className="mt-0.5 font-mono text-[10px] uppercase tracking-wider text-[#8fa4f0]">
                {u.role}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {(user?.real_user?.role || user?.role) === "superadmin" && (
                <Button
                  variant="outline"
                  onClick={() => startLive(u)}
                  data-testid={`liveview-btn-${u.user_id}`}
                  title="Watch their app live"
                  className="border-white/20 bg-transparent text-white hover:bg-white/10"
                >
                  <Eye className="mr-2 h-4 w-4" /> Live
                </Button>
              )}
              <Button
                onClick={() => impersonate(u)}
                disabled={switchingId === u.user_id}
                data-testid={`impersonate-btn-${u.user_id}`}
                className="shrink-0 bg-[#1a3fd1] text-white hover:bg-[#1a3fd1]/85"
              >
                <UserCog className="mr-2 h-4 w-4" />
                {switchingId === u.user_id ? "Switching…" : "Impersonate"}
              </Button>
            </div>
          </div>
        ))}
      {users.length <= 1 && (
        <p className="text-sm text-white/40">No other users to impersonate yet.</p>
      )}
      {live && (
        <LiveViewModal
          sessionId={live.sessionId}
          targetName={live.targetName}
          onClose={() => setLive(null)}
        />
      )}
    </div>
  );
};

export default ImpersonatePanel;
